"use client";

import React from "react";
import { ArrowLeft, Loader2, RefreshCw, Reply } from "lucide-react";

import { Button } from "@/components/ui/button";

import { SupportInboxMessageCard } from "./SupportInboxMessageCard";
import { avatarTone, initialsOf } from "./support-inbox-format";
import type {
  ReadingMessage,
  SupportInboxCorrespondent,
  SupportInboxThreadSummary,
} from "./types";

interface SupportInboxReadingPaneProps {
  companyId: string;
  selectedThreadId: string | null;
  selectedThread: SupportInboxThreadSummary | undefined;
  /** Who a reply in this thread will actually reach (the resolved customer). */
  correspondent: SupportInboxCorrespondent | null;
  messagesNewestFirst: ReadingMessage[];
  bodiesPending: boolean;
  bodiesFailed: boolean;
  onBack: () => void;
  onRetryBodies: () => void;
  onReply: (message: ReadingMessage) => void;
  onCreateTicket: (message: ReadingMessage) => void;
  /** The pane yields to the thread list on mobile until a thread is open. */
  threadListVisibleOnMobile: boolean;
}

/** Reading pane (right column) for the Support Inbox tab. */
export function SupportInboxReadingPane({
  companyId,
  selectedThreadId,
  selectedThread,
  correspondent,
  messagesNewestFirst,
  bodiesPending,
  bodiesFailed,
  onBack,
  onRetryBodies,
  onReply,
  onCreateTicket,
  threadListVisibleOnMobile,
}: SupportInboxReadingPaneProps) {
  const replyTitle = correspondent ? `Reply to ${correspondent.email}` : "Reply";

  return (
    <div
      className={`flex-1 md:overflow-y-auto ${
        threadListVisibleOnMobile ? "hidden md:block" : "block"
      }`}
    >
      {!selectedThreadId ? (
        <div className="hidden md:flex h-full items-center justify-center text-sm text-muted-foreground">
          Select a conversation to read it.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 md:hidden">
            <Button type="button" variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </div>

          {/* Conversation header — who this thread is with + one-tap reply that
              is guaranteed to reach the customer, not the group inbox. */}
          <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(
                  correspondent?.name ||
                    correspondent?.email ||
                    selectedThread?.subject ||
                    "?",
                )}`}
                aria-hidden
              >
                {initialsOf(correspondent?.name || correspondent?.email || "?")}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-card-foreground">
                  {selectedThread?.subject || "(no subject)"}
                </p>
                {correspondent ? (
                  <p className="truncate text-xs text-muted-foreground">
                    Replies go to{" "}
                    <span className="font-medium text-card-foreground">
                      {correspondent.email}
                    </span>
                  </p>
                ) : (
                  <p className="truncate text-xs text-muted-foreground">
                    No external customer detected in this thread.
                  </p>
                )}
              </div>
            </div>
            {messagesNewestFirst[0] && (
              <Button
                type="button"
                size="sm"
                onClick={() => onReply(messagesNewestFirst[0])}
                className="shrink-0"
                title={replyTitle}
              >
                <Reply className="w-4 h-4 mr-2" />
                Reply
              </Button>
            )}
          </div>

          {bodiesPending && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground px-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading full message…
            </div>
          )}

          {bodiesFailed && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
              <p className="text-xs text-destructive">
                Showing a preview — we couldn&apos;t load the full message.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetryBodies}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {messagesNewestFirst.length === 0 && !bodiesPending ? (
            <p className="text-sm text-muted-foreground p-2">
              No messages in this conversation.
            </p>
          ) : (
            messagesNewestFirst.map((message) => (
              <SupportInboxMessageCard
                key={message.messageId}
                companyId={companyId}
                message={message}
                replyTitle={replyTitle}
                onReply={onReply}
                onCreateTicket={onCreateTicket}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
